import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { createSelector } from '@reduxjs/toolkit';
import { envConfig } from '@src/utils/envConfig';
import {
  type CredentialsFormParams,
  Delete,
  Get,
  LoaderTalos,
  type MarketConfig,
  type Organization,
  Patch,
  Post,
  Put,
  type User,
  UserContext,
  useAuthContext,
  useConstant,
} from '@talos/kyoko';
import { isEmpty, isNil, sortBy } from 'lodash-es';
import type React from 'react';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { Provider, useSelector } from 'react-redux';
import { getUserStore, selectUserStoreUser, userApi } from './userStore';

export const UserContextProvider = (props: React.PropsWithChildren<unknown>) => {
  const userStore = useConstant(getUserStore());

  return (
    <Provider store={userStore}>
      <UserContextProviderInner>{props.children}</UserContextProviderInner>
    </Provider>
  );
};

const selectOrgApiEndpoint = createSelector(selectUserStoreUser, user => {
  return user?.OrgApiEndpoint ? `${window.location.protocol}//${user.OrgApiEndpoint}/v1` : undefined;
});

const selectOrgWsEndpoint = createSelector(selectUserStoreUser, user => {
  return user?.OrgWsEndpoint
    ? `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${user.OrgWsEndpoint}/ws/v1`
    : undefined;
});

function fetchUser() {
  return Get<User>(envConfig.VITE_AVA_API_ENDPOINT, `/user`);
}

const UserContextProviderInner = memo(function UserProvider(props: React.PropsWithChildren<unknown>) {
  const { isAuthenticated } = useAuthContext();
  const [isLoaded, setIsLoaded] = useState(false);
  const [organization, setOrganization] = useState<Organization>();

  const {
    data: user,
    isLoading,
    refetch: refreshUser,
  } = userApi.useGetUserQuery(undefined, { skip: !isAuthenticated });

  const orgApiEndpoint = useSelector(selectOrgApiEndpoint);
  const orgWsEndpoint = useSelector(selectOrgWsEndpoint);

  useEffect(() => {
    if (user != null) {
      datadogRum.setUser({
        id: user.ID as string,
        name: user.Name,
        organization: user.Organization,
        orgApiEndpoint: user.OrgApiEndpoint,
        isUserMorphed: user.IsMorphed,
        isOrgMorphed: user.UsingOrgID != null,
        usingOrgID: user.UsingOrgID,
      });
      datadogLogs.setUser({
        id: user.ID as string,
        name: user.Name,
        organization: user.Organization,
        orgApiEndpoint: user.OrgApiEndpoint,
        isUserMorphed: user.IsMorphed,
        isOrgMorphed: user.UsingOrgID,
        usingOrgID: user.UsingOrgID,
      });
    }
  }, [user]);

  // Send an API request every minute to check for a new UI Channel cookie.
  const uiChannelInterval = useRef<ReturnType<typeof setInterval> | null>(null);
  useEffect(() => {
    if (isLoaded) {
      uiChannelInterval.current = setInterval(fetchUser, 60_000);
    }
    return () => {
      if (uiChannelInterval.current != null) {
        clearInterval(uiChannelInterval.current);
        uiChannelInterval.current = null;
      }
    };
  }, [isLoaded]);

  const getOrganization = useCallback(() => {
    if (organization != null) {
      return Promise.resolve(organization);
    }
    return Get(envConfig.VITE_AVA_API_ENDPOINT, `/organization`).then((org: Organization) => {
      const sortedUsers = sortBy(org.Users ?? [], item => item.Name) ?? [];
      const newOrganization = { ...org, Users: sortedUsers };
      setOrganization(newOrganization);
      return newOrganization;
    });
  }, [organization]);

  const [createApiKeyMutation] = userApi.useCreateApiKeyMutation();
  const createApiKey = useCallback(
    (whitelistedIPs: string | undefined) => {
      const body = whitelistedIPs ? { whitelistedIPs } : {};
      return createApiKeyMutation(body).unwrap();
    },
    [createApiKeyMutation]
  );

  const [deleteApiKeyMutation] = userApi.useDeleteApiKeyMutation();
  const deleteApiKey = useCallback(() => {
    return deleteApiKeyMutation().unwrap();
  }, [deleteApiKeyMutation]);

  const [updateApiKeyWhitelistedIPsMutation] = userApi.useUpdateApiKeyWhitelistedIPsMutation();
  const updateApiKeyWhitelistedIPs = useCallback(
    (whitelistedIPs: string) => updateApiKeyWhitelistedIPsMutation({ whitelistedIPs }).unwrap(),
    [updateApiKeyWhitelistedIPsMutation]
  );

  const [updateApiKeyRolesMutation] = userApi.useUpdateApiKeyRolesMutation();
  const updateApiKeyRoles = useCallback(
    (roles: string[]) => updateApiKeyRolesMutation({ roles }).unwrap(),
    [updateApiKeyRolesMutation]
  );

  const listMarketConfigs = useCallback(() => {
    return Get(envConfig.VITE_AVA_API_ENDPOINT, `/markets`).then(addDefaultMarketGuides);
  }, []);

  const listAvailableMarketConfigs = useCallback(() => {
    return orgApiEndpoint
      ? Get<MarketConfig[]>(orgApiEndpoint, `/available-markets`).then(addDefaultMarketGuides)
      : Promise.reject('Missing orgApiEndpoint');
  }, [orgApiEndpoint]);

  const listMarketCredentials = useCallback(() => {
    return orgApiEndpoint
      ? Get(orgApiEndpoint, `/organization/markets/credentials`)
      : Promise.reject('Missing orgApiEndpoint');
  }, [orgApiEndpoint]);

  // FYI this sends the request as form data as some of these flows require us to send a file
  const createMarketCredential = useCallback(
    (marketName: string, values: { [key: string]: string | Blob }) => {
      const formData = new FormData();
      for (const key in values) {
        values[key] != null && formData.append(key, values[key]);
      }
      return orgApiEndpoint
        ? Post(
            orgApiEndpoint,
            `/organization/markets/${encodeURIComponent(marketName)}/credentials`,
            {},
            {
              headers: {},
              body: formData,
            }
          )
        : Promise.reject('Missing orgApiEndpoint');
    },
    [orgApiEndpoint]
  );

  const updateMarketCredential = useCallback(
    (marketName: string, credentialName: string, values: CredentialsFormParams) => {
      const formData = new FormData();
      for (const key in values) {
        values[key] != null && formData.append(key, values[key]);
      }
      return orgApiEndpoint
        ? Put(
            orgApiEndpoint,
            `/organization/markets/${encodeURIComponent(marketName)}/credentials/${encodeURIComponent(credentialName)}`,
            {},
            {
              headers: {},
              body: formData,
            }
          )
        : Promise.reject('Missing orgApiEndpoint');
    },
    [orgApiEndpoint]
  );

  const patchMarketCredential = useCallback(
    (marketName: string, credentialName: string, values: CredentialsFormParams) => {
      const formData = new FormData();
      for (const key in values) {
        values[key] != null && formData.append(key, values[key]);
      }
      return orgApiEndpoint
        ? Patch(
            orgApiEndpoint,
            `/organization/markets/${encodeURIComponent(marketName)}/credentials/${encodeURIComponent(credentialName)}`,
            {},
            {
              headers: {},
              body: formData,
            }
          )
        : Promise.reject('Missing orgApiEndpoint');
    },
    [orgApiEndpoint]
  );

  const deleteMarketCredential = useCallback(
    (marketName: string, credentialName: string) => {
      return orgApiEndpoint
        ? Delete(
            orgApiEndpoint,
            `/organization/markets/${encodeURIComponent(marketName)}/credentials/${encodeURIComponent(credentialName)}`
          )
        : Promise.reject('Missing orgApiEndpoint');
    },
    [orgApiEndpoint]
  );

  const orgMetadata = useCallback(() => {
    return orgApiEndpoint ? Get(orgApiEndpoint, `/organization`) : undefined;
  }, [orgApiEndpoint]);

  useEffect(() => {
    if (!isNil(user) && !isLoading && !isEmpty(orgApiEndpoint) && !isEmpty(orgWsEndpoint)) {
      setIsLoaded(true);
    }
  }, [user, isLoading, orgApiEndpoint, orgWsEndpoint]);

  if (isAuthenticated && (isLoading || !isLoaded)) {
    return <LoaderTalos />;
  }

  return (
    <UserContext.Provider
      value={{
        isLoaded,
        user: user as User,
        getOrganization,
        createApiKey,
        deleteApiKey,
        updateApiKeyWhitelistedIPs,
        updateApiKeyRoles,
        refreshUser,
        listMarketConfigs,
        listAvailableMarketConfigs,
        orgWsEndpoint: orgWsEndpoint!,
        orgApiEndpoint: orgApiEndpoint!,
        listMarketCredentials,
        updateMarketCredential,
        patchMarketCredential,
        createMarketCredential,
        deleteMarketCredential,
        orgMetadata,
      }}
    >
      {props.children}
    </UserContext.Provider>
  );
});

function addDefaultMarketGuides(markets: MarketConfig[]): MarketConfig[] {
  return markets.map(market => ({ ...market, guide: market.guide ?? [] }));
}
